import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import uuid from 'uuid-random';

import {
    addAutocompleterId,
    resetAutocompleter,
    setAutocompleteDefaultLinks,
} from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.actions';
import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import {
    AGENT_FEATURE,
    SCENARIOS_AI_FEATURE,
} from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { AutocompleteSearchButtonType } from 'SearchApp/ts/features/base-autocomplete/base-autocomplete.types';
import {
    AutocompleteExtraLink,
    AutocompleteExtraLinkType,
} from 'SearchApp/ts/types/categories-autocomplete-results.type';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { UrlsService } from 'Angular/services/core/urls.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { FeatureNeededComponent } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.component';
import { SearchBarComponent } from 'SearchApp/ts/features/base-autocomplete/components/search-bar.component';

/**
 *
 * Base Autocomplete Component
 *
 * This is the base autocomplete component that should contain sufficient functionality for most use cases.
 *
 * If you are trying to add specific functionality that won't be shared across most autocompleters, please consider
 * creating a new component that uses `BaseAutocompleteComponent` rather than adding the functionality directly to
 * `BaseAutocompleteComponent`.
 *
 * Input Properties:
 *   appendToBody: Whether to append the results to the DOM body or not. This is used when the autocompleter
 *                 needs to visually appear outside the confines component's bounding box.
 *   buttonType: 'add', 'icon-browse', 'icon-search'; determines which 'icon' to show to the user.
 *   buttonText: Text used for the search bar button.
 *   dropdownMenuClass: Class used for the dropdown menu.
 *   isInvalid: Boolean that shows invalid css in search bar.
 *   hideButton: Boolean that determines if button is hidden or shows.
 *   includeSearchIcon: Whether the magnifying glass search icon is included in the autocomplete search bar.
 *   placeholder: placeholder text to show when the input box is empty.
 *   searchType: Which autocompleter results should be fetched from.
 *   searchBlockRegex: Enable raw inputs that match the regex.
 *   searchableSecurityIds: Security IDs that are searchable.
 *   showMoreResultsUrl: Whether to show the more results icon in the categories results.
 *   isDisabled: Disables input tag and stops setShouldBlur and handleClick methods from running on SearchBarComponent.
 *   defaultResults: list of AutocompleteExtraLink items to display when autocompleter is focused but empty
 *   globalSearch: Show the search all and AI link if true.
 *  defaultOnly: bool needed for Data Independence to know if we are on RB or Quote page AND that we are set the Default View
 *
 * Output Properties:
 *   searchBlockHandler: EventEmitter that emits the input when searchBlockRegex is set and matches.
 *   searchBarBlurHandler: EventEmitter that emits the input when searchBar blur event occurs.
 *   submitHandler: EventEmitter that emits the selected AutocompleteResult.
 *   backspaceHandler: EventEmitter that emits when backspace is pressed.
 *   blurHandler: EventEmitter that emits in mapStateToComponent whether the search bar input is blurred.
 *   focusHandler: EventEmitter that emits in mapStateToComponent whether the search bar input is focused.
 *   browseClickHandler: EventEmitter that emits the value passed to it by the search bar's button click handler.
 *
 */

@Component({ standalone: false, selector: 'ycn-base-autocomplete', templateUrl: './base-autocomplete.template.html' })
export class BaseAutocompleteComponent implements OnChanges, OnDestroy {
    @Input() appendToBody!: boolean;

    @Input() buttonType!: AutocompleteSearchButtonType;

    @Input() buttonText: string = '';

    @Input() dropdownMenuClass?: string;

    @Input() defaultResults: AutocompleteExtraLink[] = [];

    @Input() globalSearch: boolean = false;

    @Input() includeSearchIcon: boolean = false;

    @Input() placeholder: string = 'Search';

    @Input() searchType!: string;

    @Input() searchBlockRegex?: string;

    @Input() showMoreResultsUrl?: boolean;

    @Input() isDisabled = false;

    @Input() isInvalid: boolean = false;

    @Input() hideButton: boolean = false;

    @Input() searchableSecurityIds: string[] = [];

    @Input() defaultOnly: boolean = false;

    @Input() listFooterTemplate: TemplateRef<any> | null = null;

    @Output() searchBlockHandler: EventEmitter<string> = new EventEmitter<string>();

    @Output() backspaceHandler: EventEmitter<void> = new EventEmitter<void>();

    @Output() blurHandler = new EventEmitter<void>();

    @Output() focusHandler = new EventEmitter<void>();

    @Output() searchBarBlurHandler = new EventEmitter<string>();

    @Output() browseClickHandler = new EventEmitter<void>();

    @Output() submitHandler = new EventEmitter<AutocompleteResult>();

    @ViewChild(FeatureNeededComponent) featureNeededComponent!: FeatureNeededComponent;

    @ViewChild(SearchBarComponent) searchBarComponent!: SearchBarComponent;

    autocompleterId: string;

    resultsType: 'list' | 'categories' = 'list';

    showResults: boolean = false;

    inputText: string = '';

    hasInputFocus: boolean = false;

    resultsExtraLinks: AutocompleteExtraLink[] = [];

    hasFixedWidth: boolean = false;

    private storeSubscription: Subscription;

    constructor(
        private featureService: FeatureService,
        private readonly store: Store<{ autocompleter: AutocompleteStore }>,
        private urlsService: UrlsService,
        private cdr: ChangeDetectorRef
    ) {
        this.autocompleterId = uuid();
        this.store.dispatch(addAutocompleterId({ autocompleterId: this.autocompleterId }));
        this.storeSubscription = this.store.select(AUTOCOMPLETER_STATE_SLICE_KEY).subscribe(this.mapStateToComponent);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.defaultResults) {
            const hasAgent = this.featureService.userHasFeatures([AGENT_FEATURE]);
            const showScenariosAILink = !hasAgent && this.featureService.userHasFeatures([SCENARIOS_AI_FEATURE]);
            this.resultsExtraLinks = [{ type: 'searchall', url: '', features_needed: [] }];

            const defaultLinks: AutocompleteExtraLink[] = changes.defaultResults.currentValue;
            const openNewTab = this.featureService.USER.authenticated && !hasAgent;

            const aiLink: AutocompleteExtraLink = {
                type: 'ai',
                url: '',
                label: 'AI Agent',
                newTab: openNewTab,
            };
            this.resultsExtraLinks.push(aiLink);
            defaultLinks.push(aiLink);

            if (showScenariosAILink) {
                const scenariosLink: AutocompleteExtraLink = {
                    type: 'scenariosai',
                    url: '',
                };
                this.resultsExtraLinks.push(scenariosLink);
                defaultLinks.push(scenariosLink);
            }

            this.store.dispatch(setAutocompleteDefaultLinks({ autocompleterId: this.autocompleterId, defaultLinks }));
        }
    }

    ngOnDestroy(): void {
        if (this.storeSubscription) {
            this.storeSubscription.unsubscribe();
        }
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const { blurred, selectedResult, showResults, categoriesResults, inputText, extraLinks } =
            state[this.autocompleterId];
        this.showResults = showResults;
        this.inputText = inputText;
        // The autocomplete-in-place component needs to know whether the search is blurred or in focus
        if (blurred) {
            this.blurHandler.emit();
        } else {
            this.focusHandler.emit();
        }

        this.hasInputFocus = !blurred;
        const hasExtralinks = extraLinks.length > 0;
        this.hasFixedWidth = (this.defaultResults || hasExtralinks) && !this.showResults;

        // Determine whether to show list or category results based on the `categoriesResults` slice of state
        this.resultsType = Object.keys(categoriesResults).length === 0 ? 'list' : 'categories';
        if (hasExtralinks || this.globalSearch) {
            this.resultsType = 'categories';
        }

        // State updates map to non-signal props. Manually trigger change detection
        // to ensure OnPush parent components update with the new state
        this.cdr.markForCheck();

        if (!selectedResult) {
            return;
        }

        this.store.dispatch(resetAutocompleter({ autocompleterId: this.autocompleterId }));
        const { type, url: oldUrl } = selectedResult;

        // If the result requires features the user is missing, display the feature needed modal.
        if (!this.featureService.userCanAccess(selectedResult)) {
            const missing_feature = this.featureService.getFirstMissingFeature(selectedResult);
            this.featureNeededComponent.openModal(missing_feature);
            return;
        }

        // For Search/AI extra links we need to send query input in the url
        let url = oldUrl;
        const queryInput = this.inputText;
        if ((type as AutocompleteExtraLinkType) === 'ai') {
            url = this.urlsService.getUrl(this.urlsService.AGENT.PAGE, undefined, { userPrompt: queryInput });
        } else if ((type as AutocompleteExtraLinkType) === 'searchall') {
            url = this.urlsService.getUrl(this.urlsService.SEARCH.SEARCH_ALL_INDEX, undefined, {
                queryInput,
                searchTypes: [],
            });
        } else if ((type as AutocompleteExtraLinkType) === 'scenariosai') {
            url = this.urlsService.getUrl(this.urlsService.HYPOTHETICALS.HYPOTHETICALS, undefined, {
                prompt: queryInput,
            });
        }
        this.submitHandler.emit({ ...selectedResult, url });
    };

    public focus(): void {
        /**
         * Allows external components (e.g. SecurityAutocompleteInPlaceComponent) to focus the search bar.
         */
        this.searchBarComponent.textInput.nativeElement.focus();
    }

    public reset(): void {
        /**
         * Allows external components (e.g. TopBarNavAutocompleteComponent) to reset the state.
         */
        this.store.dispatch(resetAutocompleter({ autocompleterId: this.autocompleterId }));
    }
}
